// <pfs-checklist> / <pfs-checklist-item>: eine Liste von Status-Zeilen (PFS-729).
// Jede Zeile = <pfs-status-dot> + Label + optionaler Wert (immer sichtbare
// Zusammenfassung). BEWUSST DUMM/präsentativ — der Host schiebt Status/Wert per
// data-* rein.
//
// Wiederverwendbar außerhalb des Steppers: Settings-Zusammenfassung, Anforderungs-/
// Onboarding-Listen ("✓ E-Mail verifiziert / ✗ MFA nicht aktiv"). Der Stepper
// komponiert die Items als seine Zeilen.
//
// <pfs-checklist-item> Attribute (alle data-*):
//   data-state   an <pfs-status-dot> durchgereicht (done/active/open/error/pending)
//   data-symbol  Symbol für open/active (z.B. Schritt-Nummer)
//   data-label   Zeilen-Label (Titel)
//   data-value   optionaler Wert rechts (z.B. gewählte Auswahl) — leer = nichts
// Default-Slot = optionaler Body unter der Zeile (z.B. Stepper-Inhalt).

import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";

const LIST_STYLES = `
${SHADOW_RESET_CSS}
:host {
  display: block;
  border: 1px solid var(--pfs-color-outline);
  border-radius: var(--pfs-radius-md, 0.5rem);
  overflow: hidden;
  background: var(--pfs-color-surface);
}
::slotted(pfs-checklist-item:not(:first-child)) {
  border-top: 1px solid var(--pfs-color-outline);
}
`;

const ITEM_STYLES = `
${SHADOW_RESET_CSS}
:host { display: block; }

.row {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.8rem 0.95rem;
}
.lbwrap { display: flex; flex-direction: column; min-width: 0; flex: 1 1 auto; }
.label { font-weight: 600; font-size: 0.9rem; color: var(--pfs-color-on-surface); }
.value {
  font-size: 0.8rem;
  color: var(--pfs-color-on-surface-subtle);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
:host([data-state="done"]) .value { color: var(--pfs-color-on-success-container); font-weight: 600; }
:host([data-state="error"]) .value { color: var(--pfs-color-on-danger-container); }

.body { padding: 0 0.95rem 0.9rem 3.5rem; }
.body:empty { display: none; }
`;

// <pfs-checklist>: reiner Container (Rahmen + Trenner), Items als Slot.
defineComponent({
  tagName: "pfs-checklist",
  mixins: [withShadow()],
  render(_host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    const style = document.createElement("style");
    style.textContent = LIST_STYLES;
    root.append(style, document.createElement("slot"));
  },
});

// <pfs-checklist-item>: die Status-Zeile (komponiert <pfs-status-dot>).
defineComponent({
  tagName: "pfs-checklist-item",
  mixins: [withShadow()],
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    const style = document.createElement("style");
    style.textContent = ITEM_STYLES;

    const row = document.createElement("div");
    row.className = "row";
    const dot = document.createElement("pfs-status-dot");
    const lbwrap = document.createElement("span");
    lbwrap.className = "lbwrap";
    const label = document.createElement("span");
    label.className = "label";
    const value = document.createElement("span");
    value.className = "value";
    lbwrap.append(label, value);
    row.append(dot, lbwrap);

    const body = document.createElement("div");
    body.className = "body";
    body.innerHTML = "<slot></slot>";

    root.append(style, row, body);

    const paint = (): void => {
      const state = host.dataset.state ?? "open";
      dot.setAttribute("data-state", state);
      if (host.dataset.symbol != null) dot.setAttribute("data-symbol", host.dataset.symbol);
      label.textContent = host.dataset.label ?? "";
      const v = host.dataset.value ?? "";
      value.textContent = v;
      value.style.display = v ? "" : "none";
    };
    paint();
    const obs = new MutationObserver(paint);
    ctx.cleanup.push(() => obs.disconnect());
    obs.observe(host, {
      attributes: true,
      attributeFilter: ["data-state", "data-symbol", "data-label", "data-value"],
    });
  },
});
